import { type ReactNode, useState } from "react";
import { cn } from "../../lib/utils";
import { Card, SegmentedControls, Text } from "../../primitives";
import { ExposureStackedBar } from "./ExposureStackedBar";
import {
	EXPOSURE_BREAKDOWN_LABEL,
	type ExposureBreakdown,
	type ExposureBreakdownView,
} from "./types";

const EMPTY_VIEWS: ExposureBreakdownView[] = [];

const DEFAULT_BREAKDOWN: ExposureBreakdown = "category";

const isBreakdownInViews = (
	id: ExposureBreakdown | undefined,
	views: ExposureBreakdownView[],
): id is ExposureBreakdown => views.some((view) => view.id === id);

export type ExposureChartProps = {
	views?: ExposureBreakdownView[];
	breakdown?: ExposureBreakdown;
	defaultBreakdown?: ExposureBreakdown;
	onBreakdownChange?: (breakdown: ExposureBreakdown) => void;
	body?: ReactNode;
	testId?: string;
};

export const ExposureTitle = ({
	title,
	updatedAtLabel,
}: {
	title: string;
	updatedAtLabel?: string;
}) => (
	<div className="flex min-w-0 items-baseline gap-2">
		<Card.Title>
			<Text type="title" color="primary">
				{title}
			</Text>
		</Card.Title>
		{updatedAtLabel !== undefined && (
			<Text type="caption" color="tertiary">
				{updatedAtLabel}
			</Text>
		)}
	</div>
);

export const ExposureChart = ({
	views = EMPTY_VIEWS,
	breakdown,
	defaultBreakdown,
	onBreakdownChange,
	header,
	caption,
	headerClassName,
	body,
	testId = "exposure",
}: ExposureChartProps & {
	header: ReactNode;
	caption?: ReactNode;
	headerClassName: string;
}) => {
	const fallback = isBreakdownInViews(DEFAULT_BREAKDOWN, views) ? DEFAULT_BREAKDOWN : views[0]?.id;
	const [uncontrolledBreakdown, setUncontrolledBreakdown] = useState<ExposureBreakdown | undefined>(
		defaultBreakdown ?? fallback,
	);
	const selectedBreakdown =
		(isBreakdownInViews(breakdown, views) ? breakdown : undefined) ??
		(isBreakdownInViews(uncontrolledBreakdown, views) ? uncontrolledBreakdown : undefined) ??
		fallback;
	const selectedView = views.find((view) => view.id === selectedBreakdown);
	const showControls = views.length > 1;
	const hasBody = body !== undefined || selectedView !== undefined;

	const handleBreakdownChange = (next: ExposureBreakdown) => {
		setUncontrolledBreakdown(next);
		onBreakdownChange?.(next);
	};

	return (
		<Card elevation="raised" className="rounded-xl" data-testid={testId}>
			<Card.Header
				className={cn(
					"flex-col items-stretch justify-start border-b-0 px-5 pt-5",
					hasBody ? "pb-1" : "rounded-b-[inherit] pb-5",
					headerClassName,
				)}
			>
				{header}
				{showControls && selectedBreakdown !== undefined && (
					<SegmentedControls<ExposureBreakdown>
						value={selectedBreakdown}
						onChange={handleBreakdownChange}
						data-testid={`${testId}-tabs`}
					>
						{views.map((view) => (
							<SegmentedControls.Segment
								key={view.id}
								value={view.id}
								data-testid={`${testId}-tab-${view.id}`}
							>
								{EXPOSURE_BREAKDOWN_LABEL[view.id]}
							</SegmentedControls.Segment>
						))}
					</SegmentedControls>
				)}
				{caption}
			</Card.Header>
			{body !== undefined ? (
				<Card.Body className="px-5 pt-2 pb-5">{body}</Card.Body>
			) : (
				selectedView !== undefined && (
					<Card.Body className="px-5 pt-2 pb-5">
						<div className="flex flex-col gap-2" data-testid={`${testId}-view-${selectedView.id}`}>
							<ExposureStackedBar
								columns={selectedView.columns}
								limit={selectedView.limit}
								overLimit={selectedView.overLimit}
								headroom={selectedView.headroom}
								showHeadroomInLegend={selectedView.showHeadroomInLegend}
								showZeroLegendRows={selectedView.showZeroLegendRows}
								showTotalsColumn={selectedView.showTotalsColumn}
								testId={`${testId}-bar`}
							/>
						</div>
					</Card.Body>
				)
			)}
		</Card>
	);
};
